'use client'

import { useState, useCallback } from 'react'
import { Upload, X, Film, FileText, Image as ImageIcon, Check, AlertCircle } from 'lucide-react'

interface FileUploadProps {
  accept?: string
  maxSize?: number // in MB
  multiple?: boolean
  onUpload: (files: File[]) => void
  onUrlAdd?: (url: string) => void
  className?: string
  uploadType?: 'video' | 'image' | 'document' | 'any'
  placeholder?: string
  showUrlInput?: boolean
}

export default function FileUpload({
  accept = '*/*',
  maxSize = 100,
  multiple = false,
  onUpload,
  onUrlAdd,
  className = '',
  uploadType = 'any',
  placeholder,
  showUrlInput = true
}: FileUploadProps) {
  const [dragActive, setDragActive] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [urlInput, setUrlInput] = useState('')
  const [showUrl, setShowUrl] = useState(false)
  const [error, setError] = useState('')

  const getIcon = () => {
    switch (uploadType) {
      case 'video':
        return <Film className="h-12 w-12 text-charcoal-400 dark:text-charcoal-600" />
      case 'image':
        return <ImageIcon className="h-12 w-12 text-charcoal-400 dark:text-charcoal-600" />
      case 'document':
        return <FileText className="h-12 w-12 text-charcoal-400 dark:text-charcoal-600" />
      default:
        return <Upload className="h-12 w-12 text-charcoal-400 dark:text-charcoal-600" />
    }
  }

  const getAcceptString = () => {
    if (accept !== '*/*') return accept
    
    switch (uploadType) {
      case 'video':
        return 'video/*,.mp4,.avi,.mov,.wmv,.flv,.webm'
      case 'image':
        return 'image/*,.jpg,.jpeg,.png,.gif,.webp,.svg'
      case 'document':
        return '.pdf,.doc,.docx,.txt,.rtf'
      default:
        return '*/*'
    }
  }

  const validateFile = (file: File) => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `File size must be less than ${maxSize}MB`
    }

    // Check file type if specific type is required
    if (uploadType !== 'any') {
      const acceptedTypes = getAcceptString().split(',').map(type => type.trim())
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
      const mimeType = file.type

      const isValidType = acceptedTypes.some(type => {
        if (type.startsWith('.')) {
          return fileExtension === type
        }
        if (type.includes('/*')) {
          return mimeType.startsWith(type.split('/')[0] + '/')
        }
        return mimeType === type
      })

      if (!isValidType) {
        return `Invalid file type. Accepted types: ${acceptedTypes.join(', ')}`
      }
    }

    return null
  }

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    setError('')

    const files = Array.from(e.dataTransfer.files)
    const validFiles: File[] = []
    
    for (const file of files) {
      const error = validateFile(file)
      if (error) {
        setError(error)
        return
      }
      validFiles.push(file)
    }

    if (validFiles.length > 0) {
      // Upload files to server
      await uploadFiles(validFiles)
    }
  }, [uploadType, maxSize])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    setError('')
    
    const files = Array.from(e.target.files || [])
    const validFiles: File[] = []
    
    for (const file of files) {
      const error = validateFile(file)
      if (error) {
        setError(error)
        return
      }
      validFiles.push(file)
    }

    if (validFiles.length > 0) {
      // Simulate upload progress
      setUploadProgress(0)
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev === null) return null
          if (prev >= 100) {
            clearInterval(interval)
            setTimeout(() => setUploadProgress(null), 500)
            return 100
          }
          return prev + 10
        })
      }, 100)

      onUpload(validFiles)
    }
  }

  const handleUrlSubmit = () => {
    if (urlInput.trim() && onUrlAdd) {
      onUrlAdd(urlInput.trim())
      setUrlInput('')
      setShowUrl(false)
    }
  }

  const uploadFiles = async (validFiles: File[]) => {
    setUploadProgress(0)
    
    try {
      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev === null) return null
          if (prev >= 90) {
            clearInterval(interval)
            return 90
          }
          return prev + 10
        })
      }, 100)

      // Call the onUpload callback with the files
      await onUpload(validFiles)
      
      // Complete the progress
      clearInterval(interval)
      setUploadProgress(100)
      
      // Reset progress after a delay
      setTimeout(() => setUploadProgress(null), 500)
    } catch (err) {
      setUploadProgress(null)
      setError(err instanceof Error ? err.message : 'Upload failed')
    }
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* File Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 ${
          dragActive
            ? 'border-rose-500 bg-rose-50 dark:bg-mauve-900/10'
            : error
            ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10'
            : 'border-charcoal-300 dark:border-charcoal-700 hover:border-rose-400 dark:hover:border-rose-600'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          multiple={multiple}
          accept={getAcceptString()}
          onChange={handleChange}
        />
        
        {uploadProgress !== null ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              {uploadProgress === 100 ? (
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="h-6 w-6 text-white" />
                </div>
              ) : (
                <div className="relative w-12 h-12">
                  <div className="w-12 h-12 bg-rose-200 dark:bg-mauve-800 rounded-full"></div>
                  <div 
                    className="absolute inset-0 bg-rose-500 rounded-full transition-all duration-200"
                    style={{ 
                      clipPath: `circle(50% at 50% 50%)`,
                      transform: `scale(${uploadProgress / 100})`
                    }}
                  ></div>
                  <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-charcoal-700 dark:text-charcoal-300">
                    {uploadProgress}%
                  </div>
                </div>
              )}
            </div>
            <p className="text-sm font-medium text-charcoal-700 dark:text-charcoal-300">
              {uploadProgress === 100 ? 'Upload Complete!' : 'Uploading...'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {getIcon()}
            <div>
              <h3 className="text-lg font-semibold text-charcoal-900 dark:text-white mb-2">
                {placeholder || `Upload ${uploadType === 'any' ? 'files' : uploadType}`}
              </h3>
              <p className="text-sm text-charcoal-600 dark:text-charcoal-400">
                Drag and drop {multiple ? 'files' : 'a file'} here, or click to browse
              </p>
              <p className="text-xs text-charcoal-500 dark:text-charcoal-500 mt-1">
                Max file size: {maxSize}MB
              </p>
            </div>
            
            <button
              type="button"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-rose-600 text-white px-6 py-3 rounded-xl hover:from-rose-600 hover:to-rose-700 shadow-lg transition-all duration-200"
            >
              <Upload className="h-4 w-4" />
              Choose {multiple ? 'Files' : 'File'}
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          </div>
        )}
      </div>

      {/* URL Input Option */}
      {showUrlInput && (
        <div className="space-y-3">
          {!showUrl ? (
            <button
              type="button"
              onClick={() => setShowUrl(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-charcoal-800 border border-charcoal-300 dark:border-charcoal-700 rounded-xl text-charcoal-700 dark:text-charcoal-300 hover:bg-charcoal-50 dark:hover:bg-charcoal-700 transition-all"
            >
              <span>Or add from URL</span>
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder={`Enter ${uploadType} URL...`}
                className="flex-1 px-4 py-3 bg-white dark:bg-charcoal-800 border border-charcoal-300 dark:border-charcoal-700 rounded-xl focus:ring-2 focus:ring-rose-500 transition-all"
              />
              <button
                type="button"
                onClick={handleUrlSubmit}
                disabled={!urlInput.trim()}
                className="px-4 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl hover:from-rose-600 hover:to-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowUrl(false)
                  setUrlInput('')
                }}
                className="px-4 py-3 bg-charcoal-200 dark:bg-charcoal-700 text-charcoal-700 dark:text-charcoal-300 rounded-xl hover:bg-charcoal-300 dark:hover:bg-charcoal-600 transition-all"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

